import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Bookmark, ChevronDown, Plus, Trash2, X } from 'lucide-react';
import {
  fetchSavedListViews, createSavedListView, deleteSavedListView,
} from '../../redux/crmSlice';
import alertService from '../../utils/alert';
import { cn } from '../../utils/cn';

/**
 * Generic "saved filters" dropdown reused across CRM list pages. Each page
 * hands in its own current filter object (whatever shape it already sends
 * to its list endpoint) and a callback to apply a saved one back.
 */
export default function SavedViewsBar({ resourceType, currentFilters, onApply }) {
  const dispatch = useDispatch();
  const views = useSelector(state => state.crm.savedListViews);
  const [open, setOpen] = useState(false);
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [name, setName] = useState('');
  const [isShared, setIsShared] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    dispatch(fetchSavedListViews({ resourceType }));
  }, [dispatch, resourceType]);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await dispatch(createSavedListView({
        resourceType, name: name.trim(), filters: currentFilters, isShared,
      })).unwrap();
      alertService.toast('success', 'View saved');
      setShowSaveForm(false);
      setName('');
      setIsShared(false);
    } catch (err) {
      alertService.error('Error', typeof err === 'string' ? err : 'Failed to save view');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (view, e) => {
    e.stopPropagation();
    const confirmed = await alertService.confirm('Delete Saved View?', `Remove "${view.name}"?`, 'warning', 'Delete');
    if (!confirmed) return;
    try {
      await dispatch(deleteSavedListView(view._id)).unwrap();
      alertService.toast('success', 'View deleted');
    } catch (err) {
      alertService.error('Error', typeof err === 'string' ? err : 'Failed to delete view');
    }
  };

  const handleApply = (view) => {
    onApply(view.filters || {});
    setOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="h-9 px-3 bg-white border border-[#C9C9C9] rounded text-[13px] font-semibold text-[#080707] hover:border-[#0176D3] hover:text-[#0176D3] transition-all flex items-center gap-2 whitespace-nowrap shrink-0"
      >
        <Bookmark size={14} /> Saved Views <ChevronDown size={14} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-[90]" onClick={() => { setOpen(false); setShowSaveForm(false); }} />
          <div className="absolute right-0 mt-1.5 w-72 bg-white border border-[#D8DDE6] rounded shadow-[0_2px_8px_rgba(0,0,0,0.16)] z-[100] overflow-hidden">
            <div className="max-h-56 overflow-y-auto divide-y divide-[#F3F2F2]">
              {views.length === 0 ? (
                <p className="text-[12px] text-[#706E6B] italic p-3">No saved views yet</p>
              ) : views.map(view => (
                <button
                  key={view._id}
                  onClick={() => handleApply(view)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-[#F4F6F9] transition-all group"
                >
                  <div>
                    <p className="text-[13px] font-bold text-[#080707]">{view.name}</p>
                    {view.isShared && <p className="text-[10px] text-[#706E6B] uppercase tracking-wider">Shared</p>}
                  </div>
                  <span
                    onClick={(e) => handleDelete(view, e)}
                    className="opacity-0 group-hover:opacity-100 text-[#706E6B] hover:text-[#BA0517] transition-all"
                  >
                    <Trash2 size={13} />
                  </span>
                </button>
              ))}
            </div>

            <div className="border-t border-[#D8DDE6] p-2.5">
              {!showSaveForm ? (
                <button
                  onClick={() => setShowSaveForm(true)}
                  className="w-full h-8 flex items-center justify-center gap-1.5 text-[12px] font-bold text-[#0176D3] hover:bg-[#F4F6F9] rounded transition-all"
                >
                  <Plus size={13} /> Save current filters
                </button>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5">
                    <input
                      autoFocus
                      type="text"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleSave()}
                      placeholder="View name"
                      className="flex-1 h-8 px-2.5 bg-white border border-[#D8DDE6] rounded text-[12px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
                    />
                    <button onClick={() => setShowSaveForm(false)} className="text-[#706E6B] hover:text-[#080707]">
                      <X size={14} />
                    </button>
                  </div>
                  <label className="flex items-center gap-1.5 text-[11px] text-[#706E6B] cursor-pointer">
                    <input type="checkbox" checked={isShared} onChange={e => setIsShared(e.target.checked)} className="accent-[#0176D3]" />
                    Share with team
                  </label>
                  <button
                    onClick={handleSave}
                    disabled={saving || !name.trim()}
                    className={cn(
                      'w-full h-8 rounded text-[12px] font-bold text-white transition-all disabled:opacity-50',
                      'bg-[#0176D3] hover:bg-[#014486]'
                    )}
                  >
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
